import React from 'react';
import axios from 'helpers/axios';
import _ from 'lodash';

import { UserLayout } from 'ui/components/layouts';
import {
  Title,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Form,
  Container,
  Label,
  TextInput,
  Button,
  ToggleSwitch,
  Separator,
  ConfirmationModal,
  ModalPosition,
} from 'ui/components/styled';
import { useUser, useToasts, useQuery, useLayout } from 'hooks';

const defaults = {
  dashboard: {
    showTimer: false,
  },
};

const supportEmail = 'support@process.watch';

const tabs = ['general', 'security', 'dashboard'];

export const Settings = () => {
  const { user, saveUser, saveSettings, redirectTo } = useUser();
  const { setLayoutLoading } = useLayout();
  const [state, setState] = React.useState({
    name: {
      first: '',
      last: '',
    },
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    verifyPassword: '',
    ...defaults,
  });
  const [showTFAModal, setShowTFAModal] = React.useState(false);
  const { errorToast, successToast, removeAllToasts } = useToasts();
  const [activeTabIndex, setActiveTabIndex] = React.useState(0);
  const [query, setQuery] = useQuery();

  React.useEffect(() => {
    let index = tabs.indexOf(query?.tab);
    if (index <= -1) index = 0;
    setActiveTabIndex(index);
  }, []);

  React.useEffect(() => {
    if (user.name) {
      const settings = {
        ...defaults,
        ...(user.data?.settings || {}),
      };
      setState({
        ...state,
        name: {
          first: user.name.first,
          last: user.name.last,
        },
        email: user.email,
        phone: user.phone,
        dashboard: settings.dashboard,
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    _.set(state, name, value);
    setState({ ...state });
  };

  const handleDashboardTimerToggle = async (e) => {
    try {
      setLayoutLoading(true);
      const { checked } = e.target;
      const settings = { dashboard: { showTimer: checked } };
      const response = await axios.post('/me/?$merge=true', { data: { settings } });
      if (response.status === 200) {
        setState((state) => ({ ...state, ...settings }));
        saveSettings(settings);
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Update settings' });
    }
    setLayoutLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLayoutLoading(true);
      switch (activeTabIndex) {
        case tabs.indexOf('general'): {
          const response = await axios.post('/me', { name: state.name });
          if (response.status === 200) {
            const { name } = response.data;
            saveUser({ ...user, name });
            successToast('Profile updated successfully!');
          }
          break;
        }
        case tabs.indexOf('security'): {
          if (state.newPassword !== state.verifyPassword) {
            errorToast('Passwords does not match!');
            break;
          }
          const response = await axios.post('/auth/password', {
            currentPassword: state.currentPassword,
            newPassword: state.newPassword,
            verifyPassword: state.verifyPassword,
          });
          if (response.status === 200) {
            successToast('Password updated successfully!');
          }
          break;
        }
        default:
          break;
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Update settings' });
    }
    setLayoutLoading(false);
  };

  const handleTabSelect = (index) => {
    removeAllToasts();
    setActiveTabIndex(index);
    setQuery({ tab: tabs[index] });
  };

  return (
    <UserLayout>
      <Title variant="dark" mx="m" mb="xxl">
        User settings
      </Title>
      <Tabs selectedIndex={activeTabIndex} onSelect={handleTabSelect}>
        <TabList>
          <Tab>General</Tab>
          <Tab>Security</Tab>
          <Tab>Dashboard</Tab>
        </TabList>
        {/* General */}
        <TabPanel>
          <Form width="400px" onSubmit={handleSubmit}>
            <Container mb="l">
              <Label htmlFor="email" mb="xs">
                Email *
              </Label>
              <TextInput
                id="email"
                name="email"
                type="email"
                placeholder="Email"
                variant="minimal"
                value={user.email}
                onChange={handleChange}
                required
                disabled
              />
            </Container>
            <Container mb="l">
              <Label htmlFor="phone" mb="xs">
                Phone number *
              </Label>
              <TextInput
                id="phone"
                name="phone"
                type="tel"
                placeholder="Phone number"
                variant="minimal"
                value={user.phone}
                onChange={handleChange}
                required
                disabled
              />
            </Container>
            <Container mb="l">
              <Label htmlFor="firstname" mb="xs">
                First name
              </Label>
              <TextInput
                id="firstname"
                name="name.first"
                placeholder="First name"
                variant="minimal"
                value={state.name.first}
                onChange={handleChange}
                required
              />
            </Container>
            <Container mb="l">
              <Label htmlFor="lastname" mb="xs">
                Last name
              </Label>
              <TextInput
                id="lastname"
                name="name.last"
                placeholder="Last name"
                variant="minimal"
                value={state.name.last}
                onChange={handleChange}
                required
              />
            </Container>
            <Button type="submit">Update</Button>
          </Form>
          <Container height="auto" mt="xl">
            <Label variant="legend">
              <span className="highlighted">*</span> Please contact Process Watch support to change
              your email or phone number <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
            </Label>
          </Container>
        </TabPanel>
        {/* Security */}
        <TabPanel>
          <Container width="400px" height="auto">
            <Form onSubmit={handleSubmit}>
              <Container mb="l">
                <Label htmlFor="current-password" mb="xs">
                  Reset Password
                </Label>
                <TextInput
                  id="current-password"
                  name="currentPassword"
                  type="password"
                  placeholder="Current password"
                  variant="minimal"
                  value={state.currentPassword}
                  onChange={handleChange}
                  required
                  mb="s"
                />
                <TextInput
                  name="newPassword"
                  type="password"
                  minLength="10"
                  placeholder="New password"
                  variant="minimal"
                  value={state.newPassword}
                  onChange={handleChange}
                  required
                  mb="s"
                />
                <TextInput
                  name="verifyPassword"
                  type="password"
                  minLength="10"
                  placeholder="Confirm password"
                  variant="minimal"
                  value={state.verifyPassword}
                  onChange={handleChange}
                  required
                />
              </Container>
              <Button type="submit">Update</Button>
            </Form>
            <Separator mt="xl" mb="l" />
            <Container>
              <Label mb="s">2FA Reset</Label>
              <Button onClick={() => setShowTFAModal(true)}>Reset</Button>
            </Container>
          </Container>
        </TabPanel>
        {/* Dashboard */}
        <TabPanel>
          <Container mb="l" height="auto">
            <Label htmlFor="dashboard" mb="xs">
              Show dashboard timer
            </Label>
            <ToggleSwitch
              id="dashboard"
              name="showTimer"
              testId="dashboard-timer-toggle"
              checked={state.dashboard.showTimer}
              onChange={handleDashboardTimerToggle}
              mb="s"
            />
            <Label variant="legend">
              The dashboard timer is used to refresh the dashboard every 30 seconds on browsers that
              do not support real-time updates through websockets.
            </Label>
          </Container>
        </TabPanel>
      </Tabs>
      <ConfirmationModal
        show={showTFAModal}
        onClose={() => setShowTFAModal(false)}
        onConfirm={() => redirectTo('/2fa/reset')}
        position={ModalPosition.TOP_CENTER}
        top="25%"
        maxWidth="450px"
      >
        Are you sure you want to reset 2FA?
      </ConfirmationModal>
    </UserLayout>
  );
};
